/**
 * InferenceChainViewer.tsx - Sequential Per-Chain Layout
 *
 * Displays all Oracle inference chains as a deduplicated graph where each
 * chain is laid out as a distinct left-to-right horizontal path.
 *
 * Features:
 * - Zero node/edge duplication across chains (shared nodes converge)
 * - Each chain reads as a clear sequential causal path
 * - BSC perspective color legend (clickable for filtering)
 * - Bezier edges with strokeWidth scaled by chainRefs
 * - Click-to-highlight connected subgraph
 * - Double-click for cross-tab navigation to Business Graph
 * - Edge legend bar showing evidence strength
 */

import { useCallback, useMemo, useState, useEffect } from 'react'
import { useTheme } from '@contexts/ThemeContext'
import { themeColors } from '@config/themeColors'
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  NodeMouseHandler,
} from 'reactflow'
import 'reactflow/dist/style.css'

import type { InferenceChain, BSCPerspective, ChainExplanation, NodeMetric, NodeKSA, NodeRefDoc } from '@models/oracle'
import { consolidateChains, normalizeNodeId } from '@utils/chainConsolidator'
import type { ConsolidatedGraph, ConsolidatedNode } from '@models/oracle'
import ChainNode from './ChainNode'
import './InferenceChainViewer.css'

// Custom node types
const nodeTypes = {
  chainNode: ChainNode,
}

// BSC perspective colors
const BSC_COLORS: Record<string, string> = {
  'Learning_Growth': '#3b82f6',
  'Internal_Process': '#10b981',
  'Customer': '#f97316',
  'Financial': '#ef4444',
  'unknown': '#6b7280',
}

const BSC_LABELS: Record<string, string> = {
  'Learning_Growth': 'Learning & Growth',
  'Internal_Process': 'Internal Process',
  'Customer': 'Customer',
  'Financial': 'Financial',
  'unknown': 'Other',
}

// Node dimensions
const NODE_WIDTH = 200
const NODE_HEIGHT = 130

// Sequential layout constants
const NODE_X_GAP = 30    // horizontal gap between nodes in a chain
const ROW_GAP = 60       // vertical gap between chain rows
const MARGIN_X = 40
const MARGIN_Y = 40

interface InferenceChainViewerProps {
  chains: InferenceChain[]
  selectedChainIndex: number
  onSelectChain: (index: number) => void
  onNodeDoubleClick?: (nodeId: string) => void
  chainExplanations?: ChainExplanation[]
  nodeMetrics?: Record<string, NodeMetric[]>  // KPI Grounding: Metrics per node
  nodeKsas?: Record<string, NodeKSA[]>  // Competency Grounding: KSAs per node
  nodeRefDocs?: Record<string, NodeRefDoc[]>  // Evidence Grounding: Ref_Documents per node
}

/**
 * Build sequential per-chain layout from raw chains + consolidated graph.
 *
 * Each chain is a horizontal row. Nodes are placed left-to-right in chain order.
 * Shared (deduplicated) nodes appear at their first-placed position — later chains
 * draw edges to them and continue on their own row.
 */
function buildSequentialChainLayout(
  chains: InferenceChain[],
  consolidated: ConsolidatedGraph,
  nodeMetrics?: Record<string, NodeMetric[]>,
  nodeKsas?: Record<string, NodeKSA[]>,
  nodeRefDocs?: Record<string, NodeRefDoc[]>,
): { nodes: Node[]; edges: Edge[] } {

  const nodeStep = NODE_WIDTH + NODE_X_GAP
  const rowStep = NODE_HEIGHT + ROW_GAP

  // Lookup from nodeId → ConsolidatedNode
  const nodeLookup = new Map<string, ConsolidatedNode>()
  consolidated.nodes.forEach(n => nodeLookup.set(n.id, n))

  // Track placed node positions
  const placedPositions = new Map<string, { x: number; y: number }>()
  const rfNodes: Node[] = []

  // Place nodes per chain
  chains.forEach((chain, chainIndex) => {
    const rowY = MARGIN_Y + chainIndex * rowStep

    // Reconstruct sequential path: anchor → matched_nodes
    const anchorId = normalizeNodeId(chain.anchor_concept)
    const matchedIds = chain.matched_nodes.map(id => normalizeNodeId(id))

    // Deduplicate within this chain's sequence
    const seen = new Set<string>()
    const path: string[] = []
    ;[anchorId, ...matchedIds].forEach(id => {
      if (!seen.has(id)) {
        seen.add(id)
        path.push(id)
      }
    })

    // Append gaps after matched nodes
    chain.gaps.forEach(g => {
      const gapId = `gap:${g}`
      if (!seen.has(gapId)) {
        seen.add(gapId)
        path.push(gapId)
      }
    })

    let nextX = MARGIN_X

    path.forEach(nodeId => {
      const cNode = nodeLookup.get(nodeId)
      if (!cNode) return

      if (placedPositions.has(nodeId)) {
        // Shared node — already placed. Advance nextX past it so subsequent
        // nodes in this chain are positioned to the right of the shared node.
        const existing = placedPositions.get(nodeId)!
        nextX = Math.max(nextX, existing.x + nodeStep)
      } else {
        // Place new node
        placedPositions.set(nodeId, { x: nextX, y: rowY })

        rfNodes.push({
          id: cNode.id,
          type: 'chainNode',
          position: { x: nextX, y: rowY },
          data: {
            concept: cNode.concept,
            nodeId: cNode.isGap ? null : cNode.id,
            perspective: cNode.perspective,
            isAnchor: cNode.isAnchor,
            isGap: cNode.isGap,
            confidence: cNode.isGap ? 0 : 0.85 + (cNode.chainRefs / consolidated.totalOriginalChains) * 0.1,
            chainRefs: cNode.chainRefs,
            totalChains: consolidated.totalOriginalChains,
            sourceChainIndices: cNode.sourceChainIndices,
            metrics: nodeMetrics?.[cNode.id] || [],  // KPI Grounding
            ksas: nodeKsas?.[cNode.id] || [],  // Competency Grounding
            ref_docs: nodeRefDocs?.[cNode.id] || [],  // Evidence Grounding
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
        })

        nextX += nodeStep
      }
    })
  })

  // Build edges from consolidated (already deduplicated) edges
  // Only include edges where both nodes are placed
  const maxEdgeWeight = Math.max(1, ...consolidated.edges.map(e => e.chainRefs))

  const rfEdges: Edge[] = consolidated.edges
    .filter(e => placedPositions.has(e.sourceId) && placedPositions.has(e.targetId))
    .map(edge => {
      const sourceNode = nodeLookup.get(edge.sourceId)
      const strokeWidth = 1 + (edge.chainRefs / maxEdgeWeight) * 3
      const isHighWeight = edge.chainRefs >= 3
      const color = BSC_COLORS[sourceNode?.perspective || 'unknown'] || '#4b5563'

      return {
        id: `${edge.sourceId}→${edge.targetId}`,
        source: edge.sourceId,
        target: edge.targetId,
        type: 'default', // bezier
        animated: isHighWeight,
        style: {
          stroke: color,
          strokeWidth,
          opacity: 0.4 + (edge.chainRefs / maxEdgeWeight) * 0.6,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          width: 15,
          height: 15,
        },
        data: {
          chainRefs: edge.chainRefs,
          relationship: edge.relationship,
        },
      }
    })

  return { nodes: rfNodes, edges: rfEdges }
}

function InferenceChainViewer({
  chains,
  selectedChainIndex: _selectedChainIndex,
  onSelectChain: _onSelectChain,
  onNodeDoubleClick: onNodeDoubleClickProp,
  chainExplanations,
  nodeMetrics,
  nodeKsas,
  nodeRefDocs,
}: InferenceChainViewerProps) {

  const { theme } = useTheme()
  const rfColors = themeColors[theme].reactFlow

  // Track which chain narratives are expanded (collapsed by default if text is long)
  const [expandedNarratives, setExpandedNarratives] = useState<Set<number>>(new Set())

  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null)
  const [highlightedPerspective, setHighlightedPerspective] = useState<BSCPerspective | 'unknown' | null>(null)

  // Consolidate chains into single deduplicated graph
  const consolidated = useMemo(() => consolidateChains(chains), [chains])

  // Build layout
  const { layoutNodes, layoutEdges } = useMemo(() => {
    const result = buildSequentialChainLayout(chains, consolidated, nodeMetrics, nodeKsas, nodeRefDocs)
    return {
      layoutNodes: result.nodes,
      layoutEdges: result.edges,
    }
  }, [chains, consolidated, nodeMetrics, nodeKsas, nodeRefDocs])

  // Build set of node IDs in highlighted perspective (for edge filtering)
  const perspectiveNodeIds = useMemo(() => {
    if (!highlightedPerspective) return null
    const ids = new Set<string>()
    layoutNodes.forEach(node => {
      if (node.data?.perspective === highlightedPerspective) ids.add(node.id)
    })
    return ids
  }, [layoutNodes, highlightedPerspective])

  // Apply highlight/dim state — perspective filter takes priority over node highlight
  const displayNodes = useMemo(() => {
    if (highlightedPerspective) {
      return layoutNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          isDimmed: node.data?.perspective !== highlightedPerspective,
          isHighlighted: false,
        },
      }))
    }

    if (!highlightedNodeId) return layoutNodes

    // Find all nodes connected to the highlighted node
    const connectedIds = new Set<string>([highlightedNodeId])
    layoutEdges.forEach(edge => {
      if (edge.source === highlightedNodeId) connectedIds.add(edge.target)
      if (edge.target === highlightedNodeId) connectedIds.add(edge.source)
    })

    return layoutNodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isDimmed: !connectedIds.has(node.id),
        isHighlighted: node.id === highlightedNodeId,
      },
    }))
  }, [layoutNodes, layoutEdges, highlightedNodeId, highlightedPerspective])

  const displayEdges = useMemo(() => {
    if (highlightedPerspective && perspectiveNodeIds) {
      return layoutEdges.map(edge => {
        const touchesPerspective = perspectiveNodeIds.has(edge.source) || perspectiveNodeIds.has(edge.target)
        return {
          ...edge,
          style: {
            ...edge.style,
            opacity: touchesPerspective ? (edge.style?.opacity as number || 0.7) : 0.08,
          },
        }
      })
    }

    if (!highlightedNodeId) return layoutEdges

    return layoutEdges.map(edge => {
      const isConnected = edge.source === highlightedNodeId || edge.target === highlightedNodeId
      return {
        ...edge,
        style: {
          ...edge.style,
          opacity: isConnected ? 1.0 : 0.1,
          strokeWidth: isConnected ? (edge.style?.strokeWidth as number || 2) + 1 : edge.style?.strokeWidth,
        },
      }
    })
  }, [layoutEdges, highlightedNodeId, highlightedPerspective, perspectiveNodeIds])

  const [nodes, setNodes, onNodesChange] = useNodesState(displayNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(displayEdges)

  useEffect(() => {
    setNodes(displayNodes)
    setEdges(displayEdges)
  }, [displayNodes, displayEdges, setNodes, setEdges])

  // Handle node click — toggle highlight
  const handleNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    setHighlightedNodeId(prev => prev === node.id ? null : node.id)
    setHighlightedPerspective(null)  // clear perspective filter on node click
  }, [])

  // Handle node double-click for cross-tab navigation
  const handleNodeDoubleClick: NodeMouseHandler = useCallback((_event, node) => {
    if (node.data.nodeId && !node.data.isGap && onNodeDoubleClickProp) {
      onNodeDoubleClickProp(node.data.nodeId)
    }
  }, [onNodeDoubleClickProp])

  // Clear highlight on pane click
  const handlePaneClick = useCallback(() => {
    setHighlightedNodeId(null)
    setHighlightedPerspective(null)
  }, [])

  // Toggle perspective filter on legend click
  const handleLegendClick = useCallback((perspective: BSCPerspective | 'unknown') => {
    setHighlightedPerspective(prev => prev === perspective ? null : perspective)
    setHighlightedNodeId(null)
  }, [])

  // Determine which perspectives have nodes (for legend)
  const activePerspectives = useMemo(() => {
    const perspectives = new Set<string>()
    consolidated.nodes.forEach(n => perspectives.add(n.perspective))
    return perspectives
  }, [consolidated])

  // Compute stats for the bottom bar
  const edgeStats = useMemo(() => {
    const stats: Record<string, number> = {}
    consolidated.edges.forEach(edge => {
      const sourceNode = consolidated.nodes.find(n => n.id === edge.sourceId)
      const targetNode = consolidated.nodes.find(n => n.id === edge.targetId)
      if (sourceNode && targetNode && sourceNode.perspective !== targetNode.perspective) {
        const key = `${BSC_LABELS[sourceNode.perspective] || sourceNode.perspective} → ${BSC_LABELS[targetNode.perspective] || targetNode.perspective}`
        stats[key] = (stats[key] || 0) + edge.chainRefs
      }
    })
    return stats
  }, [consolidated])

  const totalUniqueNodes = consolidated.nodes.length
  const totalUniqueEdges = consolidated.edges.length

  return (
    <div className={`inference-chain-viewer ${chainExplanations?.length ? 'has-narratives' : ''}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.15,
          maxZoom: 1.2,
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color={rfColors.border} gap={20} />
        <Controls />
      </ReactFlow>

      {/* Consolidated stats bar — hidden when narratives are visible */}
      {(!chainExplanations || chainExplanations.length === 0) && (
        <div className="consolidated-stats-bar">
          <div className="stats-summary">
            <span className="stat-item">
              <strong>{totalUniqueNodes}</strong> unique nodes
            </span>
            <span className="stat-divider">|</span>
            <span className="stat-item">
              <strong>{totalUniqueEdges}</strong> unique edges
            </span>
            <span className="stat-divider">|</span>
            <span className="stat-item">
              from <strong>{consolidated.totalOriginalChains}</strong> chains
            </span>
          </div>

          {/* Cross-perspective flow legend */}
          {Object.keys(edgeStats).length > 0 && (
            <div className="flow-legend">
              {Object.entries(edgeStats)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 4)
                .map(([label, weight]) => (
                  <span key={label} className="flow-legend-item">
                    {label}: <strong>{weight}</strong>
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {/* BSC Perspective Color Legend — clickable for perspective filter */}
      <div className="bsc-legend">
        <div className="legend-title">Perspectives</div>
        <div className="legend-items">
          {Object.entries(BSC_LABELS)
            .filter(([key]) => activePerspectives.has(key))
            .map(([perspective, label]) => (
              <div
                key={perspective}
                className={`legend-item ${highlightedPerspective === perspective ? 'active' : ''}`}
                onClick={() => handleLegendClick(perspective as BSCPerspective | 'unknown')}
              >
                <span
                  className="legend-swatch"
                  style={{ backgroundColor: BSC_COLORS[perspective] }}
                />
                <span>{label}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Edge weight legend */}
      <div className="edge-legend">
        <div className="legend-title">Edge Strength</div>
        <div className="legend-items">
          <div className="legend-item">
            <svg width="30" height="8"><line x1="0" y1="4" x2="30" y2="4" stroke="#9ca3af" strokeWidth="1" /></svg>
            <span>1 chain</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="8"><line x1="0" y1="4" x2="30" y2="4" stroke="#9ca3af" strokeWidth="2.5" /></svg>
            <span>2-3 chains</span>
          </div>
          <div className="legend-item">
            <svg width="30" height="8"><line x1="0" y1="4" x2="30" y2="4" stroke="#9ca3af" strokeWidth="4" /></svg>
            <span>4+ chains</span>
          </div>
        </div>
      </div>

      {/* Per-chain narrative summaries */}
      {chainExplanations && chainExplanations.length > 0 && (
        <div className="chain-narratives">
          <div className="chain-narratives-header">Chain Narratives</div>
          {chainExplanations.map((explanation, i) => {
            if (!explanation?.narrative) return null
            const isLong = explanation.narrative.length > 200
            const isExpanded = expandedNarratives.has(i)
            const strengthColor = {
              STRONG: '#10b981',
              MODERATE: '#f59e0b',
              SPECULATIVE: '#6b7280',
              WEAK: '#ef4444',
            }[explanation.strength] || '#6b7280'

            return (
              <div key={i} className="chain-narrative-card">
                <div className="chain-narrative-header">
                  <span className="chain-label">Chain {i + 1}</span>
                  <span className="chain-narrative-anchor">{explanation.anchor}</span>
                  <span
                    className="chain-strength-badge"
                    style={{ color: strengthColor, borderColor: strengthColor }}
                  >
                    {explanation.strength}
                  </span>
                </div>
                <p className={`chain-narrative-text ${isLong && !isExpanded ? 'truncated' : ''}`}>
                  {explanation.narrative}
                </p>
                {isLong && (
                  <button
                    className="chain-narrative-toggle"
                    onClick={() => {
                      setExpandedNarratives(prev => {
                        const next = new Set(prev)
                        if (next.has(i)) next.delete(i)
                        else next.add(i)
                        return next
                      })
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default InferenceChainViewer
